import { useEffect, useState } from "react";
import {
  api,
  ApiError,
  type Filters,
  type TicketBookingPage,
  type TicketBookingRow,
  type TicketSlice,
  type TicketSourceKey,
  type TicketSourceReport,
} from "../lib/api.ts";
import type { DeltaFn } from "../lib/compare.ts";
import { money, num, pctValue } from "../lib/format.ts";
import { Card, Kpi } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { useDrillOpener } from "./dealList.tsx";
import { DrawerActions } from "./exportButton.tsx";
import { exportToExcel, fetchAllPages, moneyValue, type ExportColumn } from "../lib/tableExport.ts";
import { currentLang, plural, t, type L } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";

/**
 * Онлайн-билеты по источникам — сквозная аналитика сайта билетов на экране
 * Marketing.
 *
 * Сайт с 28.08.2026 пишет в каждую бронь, откуда пришёл человек. Источник
 * определяется одним представлением (`core.v_ticket_booking_source`, миграция
 * 093), и этот блок только показывает его: сводку, разрез по источникам,
 * разрез по рекламным кампаниям и список броней под любой цифрой.
 *
 * Две вещи блок говорит прямо, потому что без них цифры читаются неверно:
 *
 *   «Наши сайты» — не канал, а промежуточный шаг. Человек пришёл на сайт
 *   билетов с сайта бренда, а откуда он попал туда — сайт билетов не знает. В
 *   сентябре это больше половины оплат, и за ними скрыта часть рекламы.
 *
 *   ROMI по кампании — нижняя граница: в нём только оплаты, донёсшие метку
 *   кампании. Рядом стоит число покупок, которое насчитала сама Meta, — разрыв
 *   между ними и есть то, что метка теряет по дороге.
 */

/** Проза этого блока. Один блок в начале файла — см. App.tsx. */
const TX = {
  title: { ru: "Онлайн-билеты по источникам", en: "Online tickets by source" },
  hint: { ru: "сайт билетов: откуда пришли покупатели", en: "ticket site: where buyers came from" },

  /* --- плитки */
  attempts: { ru: "Попытки покупки", en: "Purchase attempts" },
  footAttempts: { ru: "брони, созданные в периоде", en: "bookings created in the period" },
  paid: { ru: "Оплачено", en: "Paid" },
  conversion: { ru: "конверсия", en: "conversion" },
  ticketsWord: {
    one: { ru: "билет", en: "ticket" },
    few: { ru: "билета", en: "tickets" },
    many: { ru: "билетов", en: "tickets" },
  },
  revenue: { ru: "Выручка онлайн-билетов", en: "Online ticket revenue" },
  avgCheck: { ru: "средний чек", en: "average order" },
  channelKnown: { ru: "Канал известен", en: "Channel known" },
  viaOwnSites: { ru: "через наши сайты", en: "via our websites" },
  notPassed: { ru: "не передан", en: "not passed" },

  /* --- таблица источников */
  colSource: { ru: "Источник", en: "Source" },
  colInside: { ru: "Что внутри", en: "What is inside" },
  colAttempts: { ru: "Попыток", en: "Attempts" },
  colPaid: { ru: "Оплачено", en: "Paid" },
  colConversion: { ru: "Конверсия", en: "Conversion" },
  colTickets: { ru: "Билетов", en: "Tickets" },
  colRevenue: { ru: "Выручка, AED", en: "Revenue, AED" },
  colShare: { ru: "Доля выручки", en: "Revenue share" },
  colAvg: { ru: "Средний чек, AED", en: "Average order, AED" },
  more: { ru: "ещё", en: "more" },
  total: { ru: "Итого", en: "Total" },
  noData: { ru: "За этот период броней на сайте нет.", en: "No bookings on the site in this period." },

  sinceNoteA: { ru: "Сайт записывает источник с", en: "The site has been recording the source since" },
  sinceNoteB: {
    ru: "по Дубаю. Брони раньше этого — в строке «До записи источников»: откуда они, неизвестно.",
    en: "Dubai time. Earlier bookings sit in the “Before source tracking” row: where they came from is unknown.",
  },
  ownSiteNoteA: { ru: "«Наши сайты» —", en: "“Our websites” is" },
  ownSiteNoteB: {
    ru: "оплат периода. Это не канал, а промежуточный шаг: человек пришёл на сайт билетов с сайта бренда (zamania.ae, leoloona.ae и др.), а откуда он попал на сам сайт бренда, сайт билетов не знает — метки и gclid/fbclid при переходе не передаются, кроме всплывающего окна со скидкой. Чтобы увидеть за этими оплатами рекламу, сайты брендов должны передавать метки дальше, в ссылку на сайт билетов.",
    en: "of the period’s payments. It is not a channel but a step on the way: the buyer reached the ticket site from a brand website (zamania.ae, leoloona.ae and others), and where they came to the brand website from, the ticket site does not know — tags and gclid/fbclid are not passed on, except by the discount pop-up. To see the advertising behind these payments, the brand websites need to pass the tags on into the link to the ticket site.",
  },
  definitionsNote: {
    ru: "Попытка — бронь, которую сайт создаёт при переходе к оплате; повтор после неудачной оплаты — новая попытка. Оплаты и выручка — по дню оплаты, как во всей системе: в сумме это выручка онлайн-билетов с Overview. Конверсия — доля попыток периода, закончившихся оплатой. Переключатель эпохи на этот блок не действует: это данные нашего сайта, а не CRM.",
    en: "An attempt is a booking the site creates when the buyer moves on to payment; a retry after a failed payment is a new attempt. Payments and revenue go by payment day, as everywhere in the system: together they are the online ticket revenue shown on Overview. Conversion is the share of the period’s attempts that ended in payment. The era switch does not apply here: this is our website’s data, not the CRM’s.",
  },

  /* --- кампании */
  campaignsTitle: { ru: "Реклама → оплаченные билеты", en: "Ads → paid tickets" },
  campaignsHint: {
    ru: "по кампаниям: расход кабинета и брони с меткой кампании",
    en: "by campaign: ad account spend and bookings carrying the campaign’s tag",
  },
  colCampaign: { ru: "Кампания", en: "Campaign" },
  colSpend: { ru: "Расход, AED", en: "Spend, AED" },
  colClicks: { ru: "Клики", en: "Clicks" },
  colPlatformPurchases: { ru: "Покупок по данным Meta", en: "Purchases per Meta" },
  colCostPerPaid: { ru: "Цена оплаты, AED", en: "Cost per payment, AED" },
  notConnected: { ru: "кабинет не подключён", en: "ad account not connected" },
  inConnected: { ru: "в подключённых кабинетах", en: "in connected ad accounts" },
  noCampaigns: {
    ru: "В этом периоде нет ни броней с меткой кампании, ни билетных кампаний с расходом.",
    en: "No bookings carry a campaign tag in this period, and no ticket campaigns spent money.",
  },
  metaGapA: { ru: "Meta насчитала по этим кампаниям", en: "Meta counted" },
  metaGapB: {
    ru: "покупок, а оплат с меткой кампании на сайте —",
    en: "purchases for these campaigns, while the site saw",
  },
  metaGapC: {
    ru: ". Meta считает покупку своим пикселем в своём окне атрибуции — в том числе ту, что пришла через сайт бренда без метки (строка «Наши сайты» выше). Поэтому ROMI здесь — нижняя граница: в нём только оплаты, донёсшие метку кампании.",
    en: " payments carrying the campaign tag. Meta counts a purchase with its own pixel within its own attribution window — including purchases that came through a brand website without a tag (the “Our websites” row above). So ROMI here is a lower bound: it only includes payments that carried the campaign tag.",
  },
  missingNote: {
    ru: "Кампании без названия — в рекламных кабинетах, к которым у аналитики нет доступа. Их расход неизвестен, и окупаемость по ним не посчитать, пока кабинеты не подключат:",
    en: "Campaigns without a name run in ad accounts analytics has no access to. Their spend is unknown, and their payback cannot be computed until those accounts are connected:",
  },
  paymentsWord: {
    one: { ru: "оплата", en: "payment" },
    few: { ru: "оплаты", en: "payments" },
    many: { ru: "оплат", en: "payments" },
  },
  worth: { ru: "на", en: "worth" },
  spendScopeNote: {
    ru: "Расход — вся кампания за период. Кампания, которая ведёт и на банкеты (поисковые Google), в этой таблице выглядит хуже, чем есть: её банкетные заявки здесь не видны. Билетные кампании Meta (цель «продажи») показаны и без единой брони — расход без оплат с меткой и есть то, на что стоит посмотреть.",
    en: "Spend is the whole campaign for the period. A campaign that also drives banquets (Google search) looks worse here than it is: its banquet enquiries are not visible in this table. Meta ticket campaigns (sales objective) are listed even without a single booking — spend with no tagged payments is exactly what deserves a look.",
  },

  /* --- список броней */
  drillAttempts: { ru: "попытки покупки", en: "purchase attempts" },
  drillPaid: { ru: "оплаченные брони", en: "paid bookings" },
  drillHintAttempts: { ru: "брони, созданные в периоде", en: "bookings created in the period" },
  drillHintPaid: { ru: "брони, оплаченные в периоде", en: "bookings paid in the period" },
  bookingsCounted: {
    one: { ru: "бронь", en: "booking" },
    few: { ru: "брони", en: "bookings" },
    many: { ru: "броней", en: "bookings" },
  },
  ofThemPaid: { ru: "оплачено", en: "paid" },
  totalling: { ru: "на сумму", en: "totalling" },
  noBookings: { ru: "Броней в этом срезе нет.", en: "No bookings in this slice." },
  colCreated: { ru: "Создана", en: "Created" },
  colTicket: { ru: "Билет", en: "Ticket" },
  colAmount: { ru: "Сумма", en: "Amount" },
  colStatus: { ru: "Статус", en: "Status" },
  colLead: { ru: "Лид", en: "Lead" },
  promo: { ru: "промо", en: "promo" },
  coupon: { ru: "промокод", en: "coupon" },
  paidOn: { ru: "оплачена", en: "paid" },
  firstPage: { ru: "В начало", en: "First" },
  previousPage: { ru: "Назад", en: "Previous" },
  nextPage: { ru: "Далее", en: "Next" },

  /* --- колонки выгрузки */
  exPaidAt: { ru: "Оплачена", en: "Paid at" },
  exReference: { ru: "Номер брони", en: "Booking reference" },
  exKind: { ru: "Тип билета", en: "Ticket type" },
  exPromo: { ru: "Промо-билет", en: "Promo ticket" },
  exQty: { ru: "Количество", en: "Quantity" },
  exAmountAed: { ru: "Сумма, AED", en: "Amount, AED" },
  exAmountNative: { ru: "Сумма в валюте парка", en: "Amount in park currency" },
  exCurrency: { ru: "Валюта", en: "Currency" },
  exCoupon: { ru: "Промокод", en: "Coupon" },
  exBasis: { ru: "Чем определён источник", en: "Source decided by" },
  exPlatform: { ru: "Площадка", en: "Platform" },
  exCampaignId: { ru: "ID кампании", en: "Campaign ID" },
  exReferrer: { ru: "Откуда перешли", en: "Referrer" },
  exLanding: { ru: "Первая страница", en: "Landing page" },
  exLeadId: { ru: "ID лида", en: "Lead ID" },
  exLeadLink: { ru: "Ссылка на лид", en: "Lead link" },
  yes: { ru: "да", en: "yes" },
} as const;

/** Подписи источников — ключ из `v_ticket_booking_source.source_key`. */
const SOURCE: Record<TicketSourceKey, L> = {
  google_ads: { ru: "Google Ads", en: "Google Ads" },
  meta_ads: { ru: "Meta Ads", en: "Meta Ads" },
  meta_organic: { ru: "Instagram и Facebook — органика", en: "Instagram & Facebook — organic" },
  meta_unmarked: { ru: "Instagram и Facebook — без меток", en: "Instagram & Facebook — untagged" },
  google_organic: { ru: "Поиск Google", en: "Google search" },
  own_site: { ru: "Наши сайты", en: "Our websites" },
  referral: { ru: "Другие сайты", en: "Other websites" },
  other_tag: { ru: "Другие метки", en: "Other tags" },
  service: { ru: "Служебные переходы", en: "Service visits" },
  no_source: { ru: "Источник не передан", en: "No source passed" },
  not_recorded: { ru: "До записи источников", en: "Before source tracking" },
};

/** Пояснение под неочевидными источниками — то, без чего строку прочтут неверно. */
const SOURCE_NOTE: Partial<Record<TicketSourceKey, L>> = {
  meta_unmarked: {
    ru: "переход из Meta без меток: реклама это или органика, не определить",
    en: "came from Meta without tags: ad or organic cannot be told",
  },
  own_site: {
    ru: "пришли с сайта бренда; исходный канал теряется",
    en: "came via a brand website; the original channel is lost",
  },
  service: {
    ru: "возврат со страницы оплаты, проверка тегов",
    en: "return from the payment page, tag checks",
  },
  no_source: {
    ru: "прямые заходы и пути, где сайт источник не пишет",
    en: "direct visits and paths where the site records no source",
  },
  not_recorded: {
    ru: "сайт ещё не записывал источник",
    en: "the site was not recording sources yet",
  },
};

/** Значения «что внутри», которые ключ, а не адрес или имя. */
const DETAIL: Record<string, L> = {
  promo_ticket: { ru: "промо-билеты", en: "promo tickets" },
  google_app: { ru: "приложение Google", en: "Google app" },
};

const BASIS: Record<TicketBookingRow["basis"], L> = {
  click_id: { ru: "идентификатор клика", en: "click identifier" },
  utm: { ru: "UTM-метки", en: "UTM tags" },
  referrer: { ru: "адрес перехода", en: "referring address" },
  none: { ru: "ничего не записано", en: "nothing recorded" },
  not_recorded: { ru: "до записи источников", en: "before source tracking" },
};

const STATUS: Record<string, L> = {
  paid: { ru: "оплачена", en: "paid" },
  expired: { ru: "не оплачена", en: "not paid" },
  failed: { ru: "оплата не прошла", en: "payment failed" },
  cancelled: { ru: "отменена", en: "cancelled" },
  refunded: { ru: "возврат", en: "refunded" },
};

export type TicketDrill = {
  title: string;
  hint?: string;
  slice: TicketSlice;
  filters?: Partial<Filters>;
};

const aed = (m: string | number | null | undefined): string => money(m, "AED", { withCode: false });
const aedShort = (m: string | number | null | undefined): string =>
  money(m, "AED", { compact: true, withCode: false });

const detailLabel = (d: string): string => (DETAIL[d] !== undefined ? t(DETAIL[d] as L) : d);

/** ROMI по формуле экрана: (выручка − расход) / расход. */
function romi(revenueMinor: number, spendMinor: number): number | null {
  return spendMinor > 0 ? (100 * (revenueMinor - spendMinor)) / spendMinor : null;
}

/** «1 сентября 2026 г., 22:42» — момент по Дубаю на языке интерфейса. */
function dubaiMoment(iso: string): string {
  return new Date(iso).toLocaleString(currentLang() === "ru" ? "ru-RU" : "en-GB", {
    timeZone: "Asia/Dubai",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function TicketSourcesSection({
  report,
  from,
  delta,
}: {
  report: TicketSourceReport;
  /** Начало периода: если оно раньше начала записи источников, блок говорит об этом. */
  from: string;
  delta?: DeltaFn;
}): React.ReactElement {
  const open = useDrillOpener();
  const k = report.kpi;
  const revenueTotal = Number(k.revenue_aed_minor);

  const drill = (title: string, slice: TicketSlice): void => {
    open?.({
      kind: "tickets",
      title,
      hint: t(slice.basis === "paid" ? TX.drillHintPaid : TX.drillHintAttempts),
      slice,
    });
  };

  /** Число-ссылка на список броней. Ноль не открывается: пустой список ничего не объясняет. */
  const cell = (value: number, title: string, slice: TicketSlice): React.ReactNode =>
    open === null || value === 0 ? (
      num(value)
    ) : (
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          drill(title, slice);
        }}
      >
        {num(value)}
      </a>
    );

  const sSource = useSortable(report.bySource, {
    source: { kind: "text", get: (r) => t(SOURCE[r.source_key]) },
    attempts: { kind: "number", get: (r) => r.attempts },
    paid: { kind: "number", get: (r) => r.paid },
    conversion: { kind: "number", get: (r) => (r.attempts > 0 ? r.attempts_paid / r.attempts : null) },
    tickets: { kind: "number", get: (r) => r.tickets },
    revenue: { kind: "number", get: (r) => r.revenue_aed_minor },
    share: { kind: "number", get: (r) => (revenueTotal > 0 ? Number(r.revenue_aed_minor) / revenueTotal : null) },
    avg: { kind: "number", get: (r) => (r.paid > 0 ? Number(r.revenue_aed_minor) / r.paid : null) },
  });

  const sCampaign = useSortable(report.byCampaign, {
    campaign: { kind: "text", get: (c) => c.campaign_name ?? `#${c.campaign_id}` },
    /* Кабинет не подключён — расход неизвестен, а не ноль: в конец списка. */
    spend: { kind: "number", get: (c) => (c.in_catalog ? c.spend_aed_minor : null) },
    clicks: { kind: "number", get: (c) => (c.in_catalog ? c.clicks : null) },
    purchases: { kind: "number", get: (c) => (c.platform === "meta" && c.in_catalog ? c.platform_purchases : null) },
    attempts: { kind: "number", get: (c) => c.attempts },
    paid: { kind: "number", get: (c) => c.paid },
    revenue: { kind: "number", get: (c) => c.revenue_aed_minor },
    romi: {
      kind: "number",
      get: (c) => (c.in_catalog ? romi(Number(c.revenue_aed_minor), Number(c.spend_aed_minor)) : null),
    },
    costPerPaid: {
      kind: "number",
      get: (c) => (c.in_catalog && c.paid > 0 ? Number(c.spend_aed_minor) / c.paid : null),
    },
  });

  /* Итог кампаний — только по подключённым кабинетам: расход остальных
     неизвестен, и сложить их выручку с известным расходом значило бы завысить
     окупаемость. Неподключённые перечислены отдельно, под таблицей. */
  const known = report.byCampaign.filter((c) => c.in_catalog);
  const missing = report.byCampaign.filter((c) => !c.in_catalog);
  const sum = (rows: typeof known, key: "spend_aed_minor" | "revenue_aed_minor" | "platform_purchases" | "clicks") =>
    rows.reduce((n, c) => n + Number(c[key]), 0);
  const knownSpend = sum(known, "spend_aed_minor");
  const knownRevenue = sum(known, "revenue_aed_minor");
  const knownPaid = known.reduce((n, c) => n + c.paid, 0);
  const knownAttempts = known.reduce((n, c) => n + c.attempts, 0);
  const metaKnown = known.filter((c) => c.platform === "meta");
  const metaPurchases = sum(metaKnown, "platform_purchases");
  const metaPaid = metaKnown.reduce((n, c) => n + c.paid, 0);
  const knownRomi = romi(knownRevenue, knownSpend);

  const ownSite = report.bySource.find((r) => r.source_key === "own_site");
  const beforeTracking = report.since !== null && from < report.since.slice(0, 10);

  const details = (key: TicketSourceKey): string => {
    const rows = report.details.filter((d) => d.source_key === key);
    if (rows.length === 0) return "";
    const shown = rows.map((d) => `${detailLabel(d.detail)} ${num(d.n)}`).join(" · ");
    const kinds = rows[0]?.kinds ?? 0;
    return kinds > rows.length ? `${shown} · ${t(TX.more)} ${num(kinds - rows.length)}` : shown;
  };

  return (
    <>
      <Card title={t(TX.title)} hint={t(TX.hint)}>
        <div className="grid cols-4" style={{ marginBottom: 14 }}>
          <Kpi
            label={t(TX.attempts)}
            value={num(k.attempts)}
            foot={t(TX.footAttempts)}
            delta={delta?.("tickets.kpi.attempts")}
            drill={{
              kind: "tickets",
              title: `${t(TX.title)} — ${t(TX.drillAttempts)}`,
              hint: t(TX.drillHintAttempts),
              slice: { basis: "attempts" },
            }}
          />
          <Kpi
            label={t(TX.paid)}
            value={num(k.paid)}
            foot={
              `${t(TX.conversion)} ${k.conversion_pct === null ? "—" : pctValue(k.conversion_pct)} · ` +
              `${num(k.tickets)} ${plural(k.tickets, TX.ticketsWord)}`
            }
            delta={delta?.("tickets.kpi.paid")}
            drill={{
              kind: "tickets",
              title: `${t(TX.title)} — ${t(TX.drillPaid)}`,
              hint: t(TX.drillHintPaid),
              slice: { basis: "paid" },
            }}
          />
          <Kpi
            label={t(TX.revenue)}
            value={aedShort(k.revenue_aed_minor)}
            unit="AED"
            foot={`${t(TX.avgCheck)} ${k.avg_check_aed_minor === null ? "—" : aed(k.avg_check_aed_minor)} AED`}
            delta={delta?.("tickets.kpi.revenue_aed_minor")}
            drill={{
              kind: "tickets",
              title: `${t(TX.title)} — ${t(TX.drillPaid)}`,
              hint: t(TX.drillHintPaid),
              slice: { basis: "paid" },
            }}
          />
          {/* Доля оплат, у которых известен КАНАЛ: реклама, органика, другие
              сайты. «Наши сайты» сюда не входят — канал за ними теряется, — и
              рядом стоит их доля, чтобы было видно, откуда пробел. */}
          <Kpi
            label={t(TX.channelKnown)}
            value={k.channel_known_pct === null ? "—" : pctValue(k.channel_known_pct)}
            foot={
              `${k.own_site_pct === null ? "—" : pctValue(k.own_site_pct)} ${t(TX.viaOwnSites)} · ` +
              `${k.unknown_pct === null ? "—" : pctValue(k.unknown_pct)} ${t(TX.notPassed)}`
            }
            delta={delta?.("tickets.kpi.channel_known_pct")}
          />
        </div>

        {report.bySource.length === 0 ? (
          <div className="empty">{t(TX.noData)}</div>
        ) : (
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={sSource} col="source">{t(TX.colSource)}</Th>
                  <th>{t(TX.colInside)}</th>
                  <Th sort={sSource} col="attempts" num>{t(TX.colAttempts)}</Th>
                  <Th sort={sSource} col="paid" num>{t(TX.colPaid)}</Th>
                  <Th sort={sSource} col="conversion" num>{t(TX.colConversion)}</Th>
                  <Th sort={sSource} col="tickets" num>{t(TX.colTickets)}</Th>
                  <Th sort={sSource} col="revenue" num>{t(TX.colRevenue)}</Th>
                  <Th sort={sSource} col="share" num>{t(TX.colShare)}</Th>
                  <Th sort={sSource} col="avg" num>{t(TX.colAvg)}</Th>
                </tr>
              </thead>
              <tbody>
                {sSource.rows.map((r) => {
                  const label = t(SOURCE[r.source_key]);
                  const rev = Number(r.revenue_aed_minor);
                  const note = SOURCE_NOTE[r.source_key];
                  return (
                    <tr key={r.source_key}>
                      <td>
                        {label}
                        {note !== undefined && <div className="drill-hint">{t(note)}</div>}
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>
                        {details(r.source_key) || "—"}
                      </td>
                      <td className="num">
                        {cell(r.attempts, `${label} — ${t(TX.drillAttempts)}`, {
                          basis: "attempts",
                          source: r.source_key,
                        })}
                      </td>
                      <td className="num">
                        {cell(r.paid, `${label} — ${t(TX.drillPaid)}`, { basis: "paid", source: r.source_key })}
                      </td>
                      <td className="num">
                        {r.attempts > 0 ? pctValue((100 * r.attempts_paid) / r.attempts) : "—"}
                      </td>
                      <td className="num">{num(r.tickets)}</td>
                      <td className="num">{aed(rev)}</td>
                      <td className="num">{revenueTotal > 0 ? pctValue((100 * rev) / revenueTotal) : "—"}</td>
                      <td className="num">{r.paid > 0 ? aed(rev / r.paid) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t(TX.total)}</td>
                  <td />
                  <td className="num">{num(k.attempts)}</td>
                  <td className="num">{num(k.paid)}</td>
                  <td className="num">{k.conversion_pct === null ? "—" : pctValue(k.conversion_pct)}</td>
                  <td className="num">{num(k.tickets)}</td>
                  <td className="num">{aed(k.revenue_aed_minor)}</td>
                  <td className="num">{revenueTotal > 0 ? pctValue(100) : "—"}</td>
                  <td className="num">{k.avg_check_aed_minor === null ? "—" : aed(k.avg_check_aed_minor)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {beforeTracking && report.since !== null && (
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.sinceNoteA)} <b>{dubaiMoment(report.since)}</b> {t(TX.sinceNoteB)}
          </div>
        )}
        {ownSite !== undefined && k.paid > 0 && ownSite.paid > 0 && (
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.ownSiteNoteA)} <b>{pctValue((100 * ownSite.paid) / k.paid)}</b> {t(TX.ownSiteNoteB)}
          </div>
        )}
        <div className="note" style={{ marginTop: 12 }}>{t(TX.definitionsNote)}</div>
      </Card>

      <Card title={t(TX.campaignsTitle)} hint={t(TX.campaignsHint)}>
        {report.byCampaign.length === 0 ? (
          <div className="empty">{t(TX.noCampaigns)}</div>
        ) : (
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <Th sort={sCampaign} col="campaign">{t(TX.colCampaign)}</Th>
                  <Th sort={sCampaign} col="spend" num>{t(TX.colSpend)}</Th>
                  <Th sort={sCampaign} col="clicks" num>{t(TX.colClicks)}</Th>
                  <Th sort={sCampaign} col="purchases" num>{t(TX.colPlatformPurchases)}</Th>
                  <Th sort={sCampaign} col="attempts" num>{t(TX.colAttempts)}</Th>
                  <Th sort={sCampaign} col="paid" num>{t(TX.colPaid)}</Th>
                  <Th sort={sCampaign} col="revenue" num>{t(TX.colRevenue)}</Th>
                  <Th sort={sCampaign} col="romi" num>ROMI</Th>
                  <Th sort={sCampaign} col="costPerPaid" num>{t(TX.colCostPerPaid)}</Th>
                </tr>
              </thead>
              <tbody>
                {sCampaign.rows.map((c) => {
                  const spend = Number(c.spend_aed_minor);
                  const rev = Number(c.revenue_aed_minor);
                  const name = c.campaign_name ?? `#${c.campaign_id}`;
                  const platformName = c.platform === "google" ? "Google Ads" : "Meta Ads";
                  const r = c.in_catalog ? romi(rev, spend) : null;
                  const slice = (basis: TicketSlice["basis"]): TicketSlice => ({
                    basis,
                    campaign: { platform: c.platform, id: c.campaign_id },
                  });
                  return (
                    <tr key={`${c.platform}:${c.campaign_id}`}>
                      <td>
                        {name}
                        <div className="drill-hint">
                          {platformName}
                          {c.account_name !== null ? ` · ${c.account_name}` : ""}
                          {!c.in_catalog ? ` · ${t(TX.notConnected)}` : ""}
                        </div>
                      </td>
                      <td className="num">
                        {c.in_catalog ? aed(spend) : <span className="muted">{t(TX.notConnected)}</span>}
                      </td>
                      <td className="num">{c.in_catalog ? num(c.clicks) : "—"}</td>
                      <td className="num">
                        {c.platform === "meta" && c.in_catalog ? num(c.platform_purchases) : "—"}
                      </td>
                      <td className="num">{cell(c.attempts, `${name} — ${t(TX.drillAttempts)}`, slice("attempts"))}</td>
                      <td className="num">{cell(c.paid, `${name} — ${t(TX.drillPaid)}`, slice("paid"))}</td>
                      <td className="num">{aed(rev)}</td>
                      <td className="num" style={r !== null && r < 0 ? { color: "var(--bad)" } : undefined}>
                        {r === null ? "—" : pctValue(r)}
                      </td>
                      <td className="num">{c.in_catalog && c.paid > 0 ? aed(spend / c.paid) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              {known.length > 0 && (
                <tfoot>
                  <tr>
                    <td>
                      {t(TX.total)}
                      <div className="drill-hint">{t(TX.inConnected)}</div>
                    </td>
                    <td className="num">{aed(knownSpend)}</td>
                    <td className="num">{num(sum(known, "clicks"))}</td>
                    <td className="num">{metaKnown.length > 0 ? num(metaPurchases) : "—"}</td>
                    <td className="num">{num(knownAttempts)}</td>
                    <td className="num">{num(knownPaid)}</td>
                    <td className="num">{aed(knownRevenue)}</td>
                    <td className="num" style={knownRomi !== null && knownRomi < 0 ? { color: "var(--bad)" } : undefined}>
                      {knownRomi === null ? "—" : pctValue(knownRomi)}
                    </td>
                    <td className="num">{knownPaid > 0 ? aed(knownSpend / knownPaid) : "—"}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}

        {metaKnown.length > 0 && metaPurchases > 0 && (
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.metaGapA)} <b>{num(metaPurchases)}</b> {t(TX.metaGapB)} <b>{num(metaPaid)}</b>
            {t(TX.metaGapC)}
          </div>
        )}
        {missing.length > 0 && (
          <div className="note" style={{ marginTop: 12 }}>
            {t(TX.missingNote)}{" "}
            {missing
              .map(
                (c) =>
                  `${c.platform === "google" ? "Google" : "Meta"} #${c.campaign_id} ` +
                  `(${num(c.paid)} ${plural(c.paid, TX.paymentsWord)} ${t(TX.worth)} ${aed(c.revenue_aed_minor)} AED)`,
              )
              .join("; ")}
            .
          </div>
        )}
        <div className="note" style={{ marginTop: 12 }}>{t(TX.spendScopeNote)}</div>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------- список броней

const PAGE = 200;

/** Метки брони одной строкой: «ig / paid / 120248…» — то, что принёс переход. */
function tagLine(r: TicketBookingRow): string {
  const parts = [r.utm_source, r.utm_medium, r.utm_campaign].filter((x): x is string => x !== null && x !== "");
  return parts.join(" / ");
}

/** «07.10 18:20» по-русски, «7 Oct 18:20» по-английски: год в строке таблицы лишний. */
function stamp(local: string | null): string {
  if (local === null) return "—";
  const [d, time] = local.split(" ");
  const [, m, day] = (d ?? "").split("-").map(Number);
  if (!m || !day) return local;
  if (currentLang() === "ru") return `${String(day).padStart(2, "0")}.${String(m).padStart(2, "0")} ${time ?? ""}`;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]; // i18n-ok: английские сокращения месяцев, русская ветка выше цифрами
  return `${day} ${MONTHS[m - 1] ?? ""} ${time ?? ""}`;
}

/** Колонки выгрузки — те же, что у таблицы; вторая строка ячейки — отдельной колонкой. */
function bookingExportColumns(): Array<ExportColumn<TicketBookingRow>> {
  return [
    { title: t(TX.colCreated), kind: "datetime", width: 17, value: (r) => r.created_local },
    { title: t(TX.exPaidAt), kind: "datetime", width: 17, value: (r) => r.paid_local },
    { title: t(TX.colStatus), width: 16, value: (r) => (STATUS[r.status] !== undefined ? t(STATUS[r.status] as L) : r.status) },
    { title: t(TX.exReference), width: 14, value: (r) => r.reference },
    { title: t(UI.park), width: 24, value: (r) => r.park_name },
    { title: t(TX.colTicket), width: 26, value: (r) => r.ticket_name },
    { title: t(TX.exKind), width: 12, value: (r) => r.ticket_kind },
    { title: t(TX.exPromo), width: 10, value: (r) => (r.is_promo_ticket ? t(TX.yes) : null) },
    { title: t(TX.exQty), kind: "number", width: 10, value: (r) => r.quantity },
    { title: t(TX.exAmountAed), kind: "money", width: 12, value: (r) => moneyValue(r.total_aed_minor) },
    {
      title: t(TX.exAmountNative),
      kind: "money",
      width: 14,
      value: (r) => (r.currency !== "AED" ? moneyValue(r.total_amount_minor, r.currency) : null),
    },
    { title: t(TX.exCurrency), width: 8, value: (r) => r.currency },
    { title: t(TX.exCoupon), width: 14, value: (r) => r.coupon_code },
    { title: t(TX.colSource), width: 28, value: (r) => t(SOURCE[r.source_key]) },
    { title: t(TX.colInside), width: 28, value: (r) => (r.detail === null ? null : detailLabel(r.detail)) },
    { title: t(TX.exBasis), width: 20, value: (r) => t(BASIS[r.basis]) },
    { title: t(TX.exPlatform), width: 10, value: (r) => r.platform },
    { title: t(TX.exCampaignId), width: 20, value: (r) => r.campaign_id },
    { title: t(TX.colCampaign), width: 36, value: (r) => r.campaign_name },
    { title: "utm_source", width: 14, value: (r) => r.utm_source },
    { title: "utm_medium", width: 14, value: (r) => r.utm_medium },
    { title: "utm_campaign", width: 22, value: (r) => r.utm_campaign },
    { title: "utm_content", width: 18, value: (r) => r.utm_content },
    { title: "utm_term", width: 14, value: (r) => r.utm_term },
    { title: "gclid", width: 18, value: (r) => r.gclid },
    { title: "fbclid", width: 18, value: (r) => r.fbclid },
    { title: t(TX.exReferrer), width: 36, value: (r) => r.referrer },
    { title: t(TX.exLanding), width: 50, value: (r) => r.landing_page },
    { title: t(TX.exLeadId), kind: "number", width: 10, value: (r) => (r.crm_lead_id === null ? null : Number(r.crm_lead_id)) },
    { title: t(TX.exLeadLink), width: 50, value: (r) => r.lead_url },
  ];
}

function useEscape(open: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

export function TicketBookingDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: TicketDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<TicketBookingPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = drill === null ? "" : JSON.stringify([drill.slice, filters]);

  useEffect(() => {
    setOffset(0);
    setPage(null);
  }, [key]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .ticketBookings(filters, drill.slice, PAGE, offset)
      .then((p) => {
        if (alive) setPage(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  useEscape(drill !== null, onClose);

  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;
  /* Сортировка — только когда весь срез на одной странице, как у остальных списков. */
  const S = useSortable(
    rows,
    {
      created: { kind: "text", get: (r) => r.created_local },
      park: { kind: "text", get: (r) => r.park_name },
      ticket: { kind: "text", get: (r) => r.ticket_name },
      amount: { kind: "number", get: (r) => r.total_aed_minor },
      status: { kind: "text", get: (r) => r.status },
      source: { kind: "text", get: (r) => t(SOURCE[r.source_key]) },
    },
    { partial: total > rows.length },
  );

  if (drill === null) return null;

  /* Весь список под цифрой: целиком на экране — в отсортированном порядке, иначе со всех страниц. */
  const runExport = async (progress: (done: number, total: number) => void): Promise<void> => {
    const all =
      total <= rows.length
        ? S.rows
        : await fetchAllPages((limit, off) => api.ticketBookings(filters, drill.slice, limit, off), progress);
    await exportToExcel(drill.title, bookingExportColumns(), all);
  };

  const hint =
    loading && page === null
      ? t(UI.loading)
      : `${num(total)} ${plural(total, TX.bookingsCounted)}` +
        (drill.slice.basis === "attempts" ? ` · ${t(TX.ofThemPaid)} ${num(page?.paid ?? 0)}` : "") +
        ` · ${t(TX.totalling)} ${money(page?.sum_aed_minor ?? 0, "AED")}` +
        (drill.hint ? ` · ${drill.hint}` : "");

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">{drill.title}</div>
            <div className="drawer-hint">{hint}</div>
          </div>
          <DrawerActions onClose={onClose} exportRun={runExport} exportDisabled={loading || total === 0} />
        </div>
        <div className="drawer-body">
          {error !== null && <div className="error">{error}</div>}
          {error === null && !loading && rows.length === 0 && <div className="empty">{t(TX.noBookings)}</div>}
          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <Th sort={S} col="created">{t(TX.colCreated)}</Th>
                    <Th sort={S} col="park">{t(UI.park)}</Th>
                    <Th sort={S} col="ticket">{t(TX.colTicket)}</Th>
                    <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
                    <Th sort={S} col="status">{t(TX.colStatus)}</Th>
                    <Th sort={S} col="source">{t(TX.colSource)}</Th>
                    <th>{t(TX.colLead)}</th>
                  </tr>
                </thead>
                <tbody>
                  {S.rows.map((r) => {
                    const tags = tagLine(r);
                    return (
                      <tr key={r.booking_id}>
                        <td className="nowrap">
                          {stamp(r.created_local)}
                          {r.reference !== null && <div className="sub">{r.reference}</div>}
                        </td>
                        <td>{r.park_name ?? "—"}</td>
                        <td>
                          {r.ticket_name ?? "—"}
                          <div className="sub">
                            × {num(r.quantity)}
                            {r.coupon_code !== null ? ` · ${t(TX.coupon)} ${r.coupon_code}` : ""}
                          </div>
                          {r.is_promo_ticket && <span className="tag">{t(TX.promo)}</span>}
                        </td>
                        <td className="num">
                          {money(r.total_aed_minor, "AED", { withCode: false })}
                          {r.currency !== "AED" && (
                            <div className="sub">{money(r.total_amount_minor, r.currency, { withCode: true })}</div>
                          )}
                        </td>
                        <td>
                          {STATUS[r.status] !== undefined ? t(STATUS[r.status] as L) : r.status}
                          {r.paid_local !== null && (
                            <div className="sub">
                              {t(TX.paidOn)} {stamp(r.paid_local)}
                            </div>
                          )}
                        </td>
                        <td>
                          {t(SOURCE[r.source_key])}
                          {(r.campaign_name ?? r.detail) !== null && (
                            <div className="sub">{r.campaign_name ?? detailLabel(r.detail as string)}</div>
                          )}
                          {tags !== "" && <div className="sub">{tags}</div>}
                          {r.gclid !== null && <span className="tag" style={{ marginRight: 4 }}>gclid</span>}
                          {r.fbclid !== null && <span className="tag">fbclid</span>}
                        </td>
                        <td>
                          {r.lead_url !== null ? (
                            <a href={r.lead_url} target="_blank" rel="noreferrer">
                              #{r.crm_lead_id}
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {total > rows.length && (
            <div className="drawer-foot">
              <span style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn-ghost" disabled={offset === 0 || loading} onClick={() => setOffset(0)}>
                  {t(TX.firstPage)}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t(TX.previousPage)}
                </button>
              </span>
              <span className="sub">
                {num(offset + 1)}–{num(offset + rows.length)} {t(UI.of)} {num(total)}
              </span>
              <button
                type="button"
                className="btn-ghost"
                disabled={offset + rows.length >= total || loading}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t(TX.nextPage)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
